import { ImageName } from "@mcleod/images";
import { Api, Block, FieldUpdateEvent, ModelRow, NumberUtil } from "@mcleod/core";
import {
    BlurEvent, ClickEvent, DataSource, DataSourceAction, DataSourceExecutionEvent, Image, LookupModelSearchEvent,
    LookupModelSelectionEvent, Panel, Snackbar, Switch, Tab, TableRow, TableRowCreationEvent, TableRowDisplayEvent,
    TableRowMode, Textbox
} from "@mcleod/components";
import { LMECompanySettings } from "@mcleod/general";
import { BrltlUtil } from "@mcleod/dispatch";
import { AutogenLayoutEdiBrltlFreightItemTable } from "./autogen/AutogenLayoutEdiBrltlFreightItemTable";

interface FgiData {
    row: TableRow,
    textLength: Textbox,
    textWidth: Textbox,
    textHeight: Textbox,
    textHandlingUnits: Textbox,
    textWeight: Textbox,
    textboxLinearUnits: Textbox,
    textboxCubicUnits: Textbox
}

export class EdiBrltlFreightItemTable extends AutogenLayoutEdiBrltlFreightItemTable {
    private fgiMap = new Map<number, FgiData>();
    private _sourceEdiOrder: DataSource;
    public doBeforeCommoditySearch: (filter: any) => void;
    private maxWeight = LMECompanySettings.get().ltl_max_weight;
    private unitOfMeasure = LMECompanySettings.get().ltl_freight_dimension_uom;

    public get sourceEdiOrder(): DataSource {
        return this._sourceEdiOrder;
    }

    textCommodityBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        if (this.doBeforeCommoditySearch)
            this.doBeforeCommoditySearch(event.filter);
    }

    private textCommodityOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.commoditySelected(event.target, event.selection);
    }

    commoditySelected(textCommodity: Textbox, selectedRow: ModelRow<any>) {
        if (selectedRow != null) {
            const row = textCommodity.getRelevantModelRow();
            const tableRow = TableRow.getContainingTableRow(textCommodity);

            BrltlUtil.clearFieldsOnCommoditySelected(row);
            row.set("hazmat_unna_nbr", null);
            row.setLookupModelData("nmfc_class_code", new ModelRow("lme/dispatch/code-nmfc-class", false, { field_code: selectedRow.get("nmfc_class_code") }))
            let pieces = null;
            if (selectedRow.getBoolean("weight_is_per_piece") ||
                selectedRow.getBoolean("spots_is_per_piece") ||
                selectedRow.getBoolean("handling_units_is_per_piece")) {
                pieces = 1;
            }
            row.set("pieces", pieces);
            const fgiData = selectedRow.get("fgi_data");
            row.addAfterFieldUpdateListener((event: FieldUpdateEvent) => {
                if ("hazmat" == event.fieldName) {
                    this.configureForHazmat(tableRow, row.getBoolean("hazmat"), row.getBoolean("hazmat"));
                    const panelHazmat = tableRow.findComponentById("panelHazmat") as Panel;
                    panelHazmat.visible = row.getBoolean("hazmat");
                }
            })

            if (fgiData != null)
                row.set({ ...fgiData });

            this.calculateCubeAndDensity(this.fgiMap.get(tableRow.index), "handling_units");
            delete selectedRow.data.fgi_data;
            this.piecesUpdated(textCommodity);
            tableRow.cells[0].displayData(row, null, 0);
        }
    }

    configureForHazmat(row: TableRow, isHazmat: boolean, expandRow: boolean) {
        const commodityTextBox = row.findComponentById("textboxCommodityId") as Textbox;
        let hazmatImage = null;
        if (isHazmat)
            hazmatImage = new Image({ name: ImageName.triangleWarning, color: "error", marginLeft: 8 });
        commodityTextBox.imagePre = hazmatImage;
        const hazmatDetails = row.findComponentById("tabHazmatDetails") as Tab;
        hazmatDetails.expandCollapseTab(isHazmat, true);
        hazmatDetails.findComponentById("panelHazmat").displayData(row.data, null, 0); // workaround for displaying data in a tab on load
    }

    calculateCubeAndDensity(fgiData: FgiData, changedField?: string) {
        const data = fgiData.row.data;
        let body: any = {
            weight: data.get("weight"),
            length: data.get("length"),
            width: data.get("width"),
            height: data.get("height"),
            handling_units: data.get("handling_units"),
            nmfc_class_code: data.get("nmfc_class_code")
        }
        if (Object.values(body).every(o => o != null)) {
            body = {
                linear_units: data.get("linear_units"),
                cubic_units: data.get("cubic_units"),
                changed_field: changedField,
                ...body
            }
            data.set("suggested_nmfc_class", null);
            Api.post("lme/dispatch/fgi-calculations", body).then(result => {
                const apiData = result.data[0];
                if (apiData) {
                    data.set("cubic_units", apiData.cubic_units);
                    data.set("linear_units", apiData.linear_units);
                    data.set("density", apiData.density);
                    data.set("suggested_nmfc_class", apiData.nmfc_class_code)
                }
                if (data.get("suggested_nmfc_class"))
                    Snackbar.showWarningSnackbar(`Density does not match freight class.  The suggested freight class is ${data.get("suggested_nmfc_class")}.`);
            });
        }
    }

    piecesUpdated(textbox: Textbox) {
        const tableRow = TableRow.getContainingTableRow(textbox);
        const commodity = tableRow.data.getFirstLookupModelData("commodity_id");
        const weight = tableRow.data.get("weight");
        const handlingUnits = tableRow.data.get("handling_units");
        if (commodity != null) {
            this.setPerPieceValues(tableRow, "weight", this.getCommodityPerPiece(commodity, "weight"));
            this.setPerPieceValues(tableRow, "req_spots", this.getCommodityPerPiece(commodity, "req_spots", "spots_is_per_piece"));
            this.setPerPieceValues(tableRow, "handling_units", this.getCommodityPerPiece(commodity, "handling_units"));
        }
        const weightUpdated = weight != tableRow.data.get("weight");
        const handlingUnitsUpdated = handlingUnits != tableRow.data.get("handling_units");
        if (weightUpdated || handlingUnitsUpdated) {
            this.calculateCubeAndDensity(this.fgiMap.get(tableRow.index), handlingUnitsUpdated ? "handling_units" : "weight")
        }
    }

    textPackagingTypeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.field_table_alias = "FGI";
        event.filter.field_name = "fgi_packaging_type_code";
    }

    textPiecesOnBlur(event: BlurEvent) {
        if (event.changedWhileFocused)
            this.piecesUpdated(event.target as Textbox)
    }

    switchHazmatOnClick(event: ClickEvent) {
        const switchHazmat = event.target as Switch;
        const tableRow = TableRow.getContainingTableRow(switchHazmat);
        this.configureForHazmat(tableRow, switchHazmat.checked, switchHazmat.checked);
    }

    private textboxHazmatOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target);
        const data = tableRow.data;
        const selectedRow = event.selection;
        if (selectedRow != null) {
            data.set("hazmat_proper_shipname", selectedRow.get("hazmat_proper_shipname"));
            data.set("hazmat_class_code", selectedRow.get("hazmat_class_code"));
            data.set("hazmat_erg_number", selectedRow.get("hazmat_erg_number"));
            data.set("hazmat_packing_group", selectedRow.get("hazmat_packing_group"));
        }
    }

    tableFreightItemsOnRowCreate(event: TableRowCreationEvent) {
        const tableRow = event.target as TableRow;
        const modelRow = tableRow.data as ModelRow;
        modelRow.set("lme_order_id", this.sourceEdiOrder?.activeRow?.get("id"));
        modelRow.setLookupModelData("nmfc_class_code", new ModelRow("lme/dispatch/code-nmfc-class", false, { field_code: modelRow.get("nmfc_class_code") }))
    }

    tableFreightItemsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.target as TableRow;
        const modelRow = tableRow.data;
        modelRow.addAfterFieldUpdateListener((event: FieldUpdateEvent) => {
            if ((event.fieldName == "length" || event.fieldName == "width" || event.fieldName == "height") && event.newValue == null) {
                modelRow.setValues({ cubic_units: null, linear_units: null, density: null, suggested_nmfc_class: null })
            }
        });

        const fgiData: FgiData = {
            row: tableRow,
            textLength: tableRow.findComponentById("textboxLength") as Textbox,
            textWidth: tableRow.findComponentById("textboxWidth") as Textbox,
            textHeight: tableRow.findComponentById("textboxHeight") as Textbox,
            textHandlingUnits: tableRow.findComponentById("textboxHandlingUnits") as Textbox,
            textWeight: tableRow.findComponentById("textboxWeight") as Textbox,
            textboxLinearUnits: tableRow.findComponentById("textboxLinearUnits") as Textbox,
            textboxCubicUnits: tableRow.findComponentById("textboxCubicUnits") as Textbox
        }

        this.fgiMap.set(tableRow.index, fgiData);
        this.addDenistyListeners(fgiData);
        this.setTableRowBorder(tableRow, "subtle.light");
        tableRow.forEveryChildComponent((comp) => {
            if (BrltlUtil.freightItemTableFields.dimensionFields.includes(comp.field)) {
                if (comp instanceof Textbox && this.unitOfMeasure != null)
                    comp.caption = `${comp.caption} (${this.unitOfMeasure})`;
            } else {
                comp.required = BrltlUtil.freightItemTableFields.requiredFields.includes(comp.field)
            }
        });
        this.configureForHazmat(tableRow, tableRow.data.getBoolean("hazmat"), false);
        this.styleTab(tableRow.findComponentById("tabHazmatDetails") as Tab);
        this.addFgiTotalListeners(fgiData);
        this.calculateCubeAndDensity(fgiData);
        const textCommodity = tableRow.findComponentById("textboxCommodityId") as Textbox;
        textCommodity.addLookupModelSelectionListener(this.textCommodityOnLookupModelSelection);
        const textboxHazmatUnnaNbr = tableRow.findComponentById("textboxHazmatUnnaNbr") as Textbox;
        textboxHazmatUnnaNbr.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => this.textboxHazmatOnLookupModelSelection(event));
    }

    styleTab(tab: Tab) {
        if (tab.heading != null && tab.headingLabel != null) {
            tab.heading.setProps({ color: "subtle.light", fontBold: false, borderWidth: 0 });
            tab.headingLabel.setProps({ color: "McLeodPrimary", fontSize: "medium", fontBold: true });
        }
    }

    addFgiTotalListeners(fgiData: FgiData) {
        fgiData.row.data.addAfterFieldUpdateListener((event: FieldUpdateEvent) => {
            if (event.fieldName == "handling_units" || event.fieldName == "weight")
                this.setFgiTotals();
        });
    }

    setFgiTotals() {
        const totalWeight = BrltlUtil.getFgiTotal(this.tableFreightItems.dataSource, "weight");
        const totalHu = BrltlUtil.getFgiTotal(this.tableFreightItems.dataSource, "handling_units");
        this.setTotalCaptions(totalWeight, totalHu);
        this.validateWeight(totalWeight);
    }

    setTotalCaptions(weight: number, handlingUnits: number) {
        this.labelTotalWeight.text = NumberUtil.formatDecimal(weight ?? 0, "#,###.#") + " lbs";
        this.labelTotalUnits.text = (handlingUnits ?? 0).toString();
    }

    validateWeight(totalWeight: number): boolean {
        if (totalWeight > this.maxWeight) {
            Snackbar.showWarningSnackbar(this.getWeightMaxMsg(), { id: "maxWeight", persist: false, targetPanel: this });
            return false;
        }
        return true;
    }

    getWeightMaxMsg(): string {
        const companySettings = LMECompanySettings.get();
        const contactName = companySettings.order_contact_name;
        const contactPhone = companySettings.order_contact_phone;
        if (contactName != null && contactPhone != null)
            return `Contact ${contactName} at ${contactPhone} for shipments weighing more than ${this.maxWeight} lbs.`;
        return `Shipments cannot weigh more than ${this.maxWeight} lbs.`;
    }

    buttonAddItemOnClick(event: ClickEvent) {
        this.addFreightItemToTable().then((tableRow: TableRow) => {
            const textboxCommodityId = tableRow.findComponentById("textboxCommodityId") as Textbox;
            tableRow.scrollIntoView({ block: Block.CENTER });
            setTimeout(() => { textboxCommodityId.focus() }, 500);
        });
    }

    addFreightItemToTable(): Promise<TableRow> {
        return new Promise((resolve) => {
            this.tableFreightItems._createNewRowData().then(row => {
                const addRowResult = this.tableFreightItems.addRow(row, { mode: TableRowMode.ADD }, { display: true, addToData: true, save: false });
                resolve(addRowResult.row);
            });
        });
    }

    sourceEdiFreightGroupItemAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.setFgiTotals();
        }
    }

    private getCommodityPerPiece(commodity: ModelRow, field: string, perPieceField: string = field + "_is_per_piece"): number {
        let perPiece = null;
        if (!commodity.isNull(field) && commodity.getBoolean(perPieceField, false))
            perPiece = commodity.get(field);
        return perPiece
    }

    private setPerPieceValues(tableRow: TableRow, field: string, perPiece: number) {
        const data = tableRow.data;
        const pieces = parseInt(data.get("pieces", 0));
        if (!isNaN(pieces) && perPiece != null) {
            let value = perPiece * pieces;
            if ("req_spots" == field)
                value = Math.round(value);
            data.set(field, value);
        }
    }

    private addDenistyListeners(fgiData: FgiData) {
        const blurListener = (event: BlurEvent) => {
            if (event.changedWhileFocused)
                this.calculateCubeAndDensity(fgiData, (event.target as Textbox).field);
        };

        Object.values(fgiData).forEach(obj => {
            if (obj instanceof Textbox)
                obj.addBlurListener(blurListener);
        });
    }

    private setTableRowBorder(tableRow: TableRow, borderColor: string) {
        tableRow.setProps({ borderWidth: 1, borderRadius: 4, borderColor: borderColor, paddingTop: 12, paddingBottom: 20, marginTop: 15, borderShadow: true });
    }
}
